import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { backend } from "@/integrations/firebase/client";
import csgLogo from "@/assets/csg-logo.jpg";
import { Menu, X, Globe, Mail, ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { localizedPath, navPath } from "@/lib/localizedPaths";

const LANGUAGES: { code: "en" | "tr" | "de"; label: string; name: string }[] = [
  { code: "en", label: "EN", name: "English" },
  { code: "tr", label: "TR", name: "Türkçe" },
  { code: "de", label: "DE", name: "Deutsch" },
];

function LanguageDropdown() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  /**
   * Dil seçimi. EN ve TR'nin ayrı kanonik adresleri var; bu yüzden yalnızca
   * state değiştirmek yetmez, o dilin adresine gitmek gerekir. Aksi halde
   * kullanıcı Türkçe içeriği İngilizce URL'de görür ve o adres paylaşıldığında
   * karşı taraf İngilizce sayfayı açar.
   *
   * Karşılığı olmayan yollarda (hizmet ve blog detayları, /admin) yönlendirme
   * yapılmaz; eski davranış korunur. Almanca için ayrı URL yapısı yoktur.
   */
  const chooseLang = (code: "en" | "tr" | "de") => {
    setLang(code);
    setOpen(false);
    if (code === "de") return;
    const target = localizedPath(pathname, code);
    if (target && target !== pathname) navigate(target);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <Globe className="w-4 h-4" aria-hidden="true" />
        <span>{active.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            role="listbox"
            className="absolute right-0 top-full mt-2 min-w-[150px] rounded-md border border-border bg-background/95 backdrop-blur-md shadow-lg py-1 z-50"
          >
            {LANGUAGES.map((l) => (
              <li key={l.code} role="option" aria-selected={lang === l.code}>
                <button
                  onClick={() => chooseLang(l.code)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-2 text-sm transition-colors ${lang === l.code ? "text-gold" : "text-foreground/80 hover:text-foreground hover:bg-foreground/5"}`}
                >
                  <span>{l.name}</span>
                  {lang === l.code && <Check className="w-4 h-4" aria-hidden="true" />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const { t, lang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  // Menü linkleri aktif dilin kanonik adreslerine gider. Türkçe sayfaların
  // site içinden link alması dizine girmelerinin ön şartı.
  const homePath = navPath("/", lang);
  const contactPath = navPath("/contact", lang);
  const navLinks = [
    { label: t("nav.home"), path: homePath },
    { label: t("nav.about"), path: navPath("/about", lang) },
    { label: t("nav.services"), path: navPath("/services", lang) },
    { label: t("nav.portfolio"), path: navPath("/portfolio", lang) },
    { label: t("nav.news"), path: navPath("/news", lang) },
    { label: t("nav.contact"), path: contactPath },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const { data: { session } } = await backend.auth.getSession();
      if (!session?.user || cancelled) return;
      const { data: roleData } = await backend.from("user_roles").select("role").eq("user_id", session.user.id).eq("role", "admin").maybeSingle();
      if (!roleData || cancelled) return;
      setIsAdmin(true);
      const fetchUnread = async () => {
        const { count } = await backend.from("contact_submissions").select("id", { count: "exact", head: true }).eq("status", "new");
        if (!cancelled) setUnreadCount(count || 0);
      };
      fetchUnread();
      const channel = backend.channel("navbar-unread").on("postgres_changes", { event: "*", schema: "public", table: "contact_submissions" }, () => { fetchUnread(); }).subscribe();
      return () => { backend.removeChannel(channel); };
    };
    const cleanup = init();
    return () => { cancelled = true; cleanup?.then(fn => fn?.()); };
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-background/95 backdrop-blur-md border-b border-border" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link to={homePath} className="flex items-center">
          <img src={csgLogo} alt="CSG The Event Company" width={121} height={62} loading="eager" decoding="sync" className="h-[62px] w-auto" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link key={link.path} to={link.path} className={`text-sm font-medium tracking-wide uppercase transition-colors duration-300 ${location.pathname === link.path ? "text-gold" : "text-foreground/70 hover:text-foreground"}`}>
              {link.label}
            </Link>
          ))}
          <LanguageDropdown />
          <div className="relative">
            <Link to={contactPath} className="btn-primary text-xs py-3 px-6">{t("nav.get_quote")}</Link>
            {isAdmin && unreadCount > 0 && (
              <Link to="/admin/submissions">
                <Badge variant="destructive" className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0 min-w-[18px] h-[18px] flex items-center justify-center animate-pulse">{unreadCount}</Badge>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Toggle */}
        <div className="lg:hidden flex items-center gap-3">
          <LanguageDropdown />
          {isAdmin && unreadCount > 0 && (
            <Link to="/admin/submissions" className="relative">
              <Mail className="w-5 h-5 text-foreground" />
              <Badge variant="destructive" className="absolute -top-1.5 -right-1.5 text-[9px] px-1 py-0 min-w-[16px] h-[16px] flex items-center justify-center">{unreadCount}</Badge>
            </Link>
          )}
          <button className="text-foreground p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen}>
            {mobileOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="lg:hidden fixed inset-0 top-20 bg-background/98 backdrop-blur-xl z-40 overflow-y-auto">
            <div className="px-6 py-8 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className={`text-lg font-medium tracking-wide ${location.pathname === link.path ? "text-gold" : "text-foreground/70"}`}>
                  {link.label}
                </Link>
              ))}
              <Link to={contactPath} className="btn-primary text-center mt-4">{t("nav.get_quote")}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
